import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Divider,
  FormControlLabel,
  Checkbox,
  Alert,
  Chip,
  Paper,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomButton from "../../Button/CustomButton";
import SaveIcon from "@mui/icons-material/Save";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import { useSelector } from "react-redux";
import apiClient from "../../../services/apiClient";
import toast from "react-hot-toast";

const ApiKeyModal = ({ open, onClose, data, onSave }) => {
  const isEdit = !!data;
  const userInfo = useSelector((state) => state?.userInfo?.user);

  const [generatedKey, setGeneratedKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    name: "",
    type: "private",
    permissions: {
      asr: true,
      tts: true,
      llm: true,
      rag: true,
      recording: true,
    },
    rate_limit: {
      requests_per_minute: 60,
      concurrent_calls: 2,
    },
    ivr_config: {
      default_llm: "llama-3.1-8b-instant",
      voice_provider: "deepgram",
      debug_mode: true,
    },
  });

  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || "",
        type: data.type?.toLowerCase() || "private",
        permissions: data.permissions || {
          asr: true,
          tts: true,
          llm: true,
          rag: true,
          recording: true,
        },
        rate_limit: data.rate_limit || {
          requests_per_minute: 60,
          concurrent_calls: 2,
        },
        ivr_config: data.ivr_config || {
          default_llm: "llama-3.1-8b-instant",
          voice_provider: "deepgram",
          debug_mode: true,
        },
      });
    }
  }, [data]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Key name is required";
    } else if (formData.name.trim().length < 3) {
      newErrors.name = "Key name must be at least 3 characters";
    } else if (formData.name.trim().length > 100) {
      newErrors.name = "Key name must not exceed 100 characters";
    }

    if (formData.rate_limit.requests_per_minute < 1) {
      newErrors.requests_per_minute = "Requests per minute must be at least 1";
    } else if (formData.rate_limit.requests_per_minute > 10000) {
      newErrors.requests_per_minute = "Requests per minute cannot exceed 10,000";
    }

    if (formData.rate_limit.concurrent_calls < 1) {
      newErrors.concurrent_calls = "Concurrent calls must be at least 1";
    } else if (formData.rate_limit.concurrent_calls > 100) {
      newErrors.concurrent_calls = "Concurrent calls cannot exceed 100";
    }

    const hasPermission = Object.values(formData.permissions).some((val) => val === true);
    if (!hasPermission) {
      newErrors.permissions = "At least one permission must be selected";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const handlePermissionChange = (permission, value) => {
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        [permission]: value,
      },
    });
    if (errors.permissions) {
      setErrors({ ...errors, permissions: null });
    }
  };

  const handleRateLimitChange = (field, value) => {
    const numValue = parseInt(value) || 0;
    setFormData({
      ...formData,
      rate_limit: {
        ...formData.rate_limit,
        [field]: numValue,
      },
    });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const handleIvrConfigChange = (field, value) => {
    setFormData({
      ...formData,
      ivr_config: {
        ...formData.ivr_config,
        [field]: value,
      },
    });
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText(generatedKey);
    toast.success("API key copied to clipboard!");
  };

  const createApiKey = async () => {
    if (!validateForm()) {
      return;
    }

    if (!userInfo?.id) {
      toast.error("User information not found. Please login again.");
      return;
    }

    setIsLoading(true);

    try {
      const requestBody = {
        name: formData.name.trim(),
        owner_id: userInfo.id,
        type: formData.type,
        permissions: formData.permissions,
        rate_limit: formData.rate_limit,
        ivr_config: formData.ivr_config,
      };

      const response = await apiClient.post("/api-keys", requestBody);

      if (response.status && response.data) {
        setGeneratedKey(response.data.api_key);
        setShowKey(true);
        toast.success(response.message || "API Key created successfully");
        onSave(response.data);
      } else {
        toast.error(response.message || "Failed to create API key");
      }
    } catch (error) {
      console.error("Error creating API key:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to create API key"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = () => {
    if (!isEdit) {
      createApiKey();
    } else {
      onSave({ ...formData });
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      type: "private",
      permissions: {
        asr: true,
        tts: true,
        llm: true,
        rag: true,
        recording: true,
      },
      rate_limit: {
        requests_per_minute: 60,
        concurrent_calls: 2,
      },
      ivr_config: {
        default_llm: "llama-3.1-8b-instant",
        voice_provider: "deepgram",
        debug_mode: true,
      },
    });
    setGeneratedKey("");
    setShowKey(false);
    setIsLoading(false);
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h5" fontWeight={600}>
            {isEdit ? "Edit API Key" : "Create New API Key"}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {!isEdit && showKey && generatedKey && (
            <Alert severity="success">
              <Typography variant="body2" fontWeight={600} gutterBottom>
                API Key Generated Successfully!
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Make sure to copy your API key now. You won't be able to see it again!
              </Typography>
              <Paper
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: "#F9FAFB",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "monospace",
                    wordBreak: "break-all",
                    flex: 1,
                  }}
                >
                  {generatedKey}
                </Typography>
                <IconButton onClick={handleCopyKey} size="small">
                  <ContentCopyIcon />
                </IconButton>
              </Paper>
            </Alert>
          )}

          {!showKey && (
            <>
              <Alert severity="info">
                API keys are used to authenticate API requests. Keep your keys secure!
              </Alert>

              <Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Key Information
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                  <TextField
                    fullWidth
                    label="Key Name"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    required
                    placeholder="e.g., Development Test Key"
                    error={!!errors.name}
                    helperText={errors.name}
                  />
                  <FormControl fullWidth>
                    <InputLabel>Key Type</InputLabel>
                    <Select
                      value={formData.type}
                      label="Key Type"
                      onChange={(e) => handleChange("type", e.target.value)}
                    >
                      <MenuItem value="private">
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            Private Key
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Server-side only, full access
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="public">
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            Public Key
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Client-side safe, limited access
                          </Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Permissions
                </Typography>
                {errors.permissions && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errors.permissions}
                  </Alert>
                )}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.permissions.asr}
                        onChange={(e) => handlePermissionChange("asr", e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          ASR (Automatic Speech Recognition)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Speech-to-text capabilities
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.permissions.tts}
                        onChange={(e) => handlePermissionChange("tts", e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          TTS (Text-to-Speech)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Voice synthesis capabilities
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.permissions.llm}
                        onChange={(e) => handlePermissionChange("llm", e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          LLM (Language Model)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          AI conversation capabilities
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.permissions.rag}
                        onChange={(e) => handlePermissionChange("rag", e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          RAG (Retrieval Augmented Generation)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Knowledge base integration
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.permissions.recording}
                        onChange={(e) => handlePermissionChange("recording", e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          Recording
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Call recording capabilities
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Rate Limiting
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                  <TextField
                    fullWidth
                    label="Requests Per Minute"
                    type="number"
                    value={formData.rate_limit.requests_per_minute}
                    onChange={(e) =>
                      handleRateLimitChange("requests_per_minute", e.target.value)
                    }
                    inputProps={{ min: 1, max: 10000 }}
                    error={!!errors.requests_per_minute}
                    helperText={
                      errors.requests_per_minute || "Maximum API requests allowed per minute (1-10,000)"
                    }
                  />
                  <TextField
                    fullWidth
                    label="Concurrent Calls"
                    type="number"
                    value={formData.rate_limit.concurrent_calls}
                    onChange={(e) =>
                      handleRateLimitChange("concurrent_calls", e.target.value)
                    }
                    inputProps={{ min: 1, max: 100 }}
                    error={!!errors.concurrent_calls}
                    helperText={
                      errors.concurrent_calls || "Maximum number of simultaneous calls (1-100)"
                    }
                  />
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  IVR Configuration
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Default LLM</InputLabel>
                    <Select
                      value={formData.ivr_config.default_llm}
                      label="Default LLM"
                      onChange={(e) => handleIvrConfigChange("default_llm", e.target.value)}
                    >
                      <MenuItem value="llama-3.1-8b-instant">Llama 3.1 8B Instant</MenuItem>
                      <MenuItem value="gpt-4">GPT-4</MenuItem>
                      <MenuItem value="gpt-3.5-turbo">GPT-3.5 Turbo</MenuItem>
                      <MenuItem value="claude-3">Claude 3</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>Voice Provider</InputLabel>
                    <Select
                      value={formData.ivr_config.voice_provider}
                      label="Voice Provider"
                      onChange={(e) => handleIvrConfigChange("voice_provider", e.target.value)}
                    >
                      <MenuItem value="deepgram">Deepgram</MenuItem>
                      <MenuItem value="elevenlabs">ElevenLabs</MenuItem>
                      <MenuItem value="google">Google TTS</MenuItem>
                      <MenuItem value="azure">Azure TTS</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.ivr_config.debug_mode}
                        onChange={(e) => handleIvrConfigChange("debug_mode", e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          Debug Mode
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Enable detailed logging for troubleshooting
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <CustomButton
          text="Cancel"
          variant="outlined"
          onClick={handleClose}
          disabled={isLoading}
        />
        {showKey ? (
          <CustomButton
            icon={<ContentCopyIcon />}
            iconPosition="left"
            text="Copy & Close"
            onClick={() => {
              handleCopyKey();
              handleClose();
            }}
          />
        ) : (
          <CustomButton
            icon={isLoading ? <CircularProgress size={20} color="inherit" /> : <VpnKeyIcon />}
            iconPosition="left"
            text={isEdit ? "Update Key" : "Create API Key"}
            onClick={handleSubmit}
            disabled={!formData.name.trim() || isLoading}
          />
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ApiKeyModal;
